import React, { useEffect, useState } from "react";
import { Pagination } from "antd";
import moment from "moment";
import { Modal, Button } from "react-bootstrap";
import api from "../../services/api";
import { notify } from "../../services/toast.config";

interface HistoryTask {
  id?: string;
}

interface TaskHistoryModalProps {
  /** The task whose history to show. `null` keeps the modal closed. */
  todo: HistoryTask | null;
  signature: string | null;
  onClose: () => void;
}

const PAGE_SIZE = 5;

const logActionMeta = (action: string) => {
  switch (action) {
    case "transfer":
      return { label: "Task Transferred", icon: "ti-transfer", color: "text-info" };
    case "update_stage":
      return { label: "Stage Updated", icon: "ti-progress", color: "text-warning" };
    case "update":
      return { label: "Task Updated", icon: "ti-edit", color: "text-primary" };
    case "delete":
      return { label: "Task Deleted", icon: "ti-trash", color: "text-danger" };
    case "create":
      return { label: "Task Created", icon: "ti-circle-plus", color: "text-success" };
    case "recurring_start":
      return { label: "Recurrence Started", icon: "ti-repeat", color: "text-success" };
    case "recurring_stop":
      return { label: "Recurrence Stopped", icon: "ti-repeat-off", color: "text-danger" };
    default:
      return { label: action, icon: "ti-point", color: "text-secondary" };
  }
};

// The backend pre-computes a compact `detail` for each log; we just render it.
const renderLogDetail = (log: any) => {
  const d = log?.detail;
  if (log.action === "transfer") {
    if (d) {
      const fromNames =
        (d.from || []).map((m: any) => m?.name).filter(Boolean).join(", ") ||
        "N/A";
      const toNames =
        (d.to || []).map((m: any) => m?.name).filter(Boolean).join(", ") ||
        "N/A";
      return (
        <>
          Transferred from <strong>{fromNames}</strong> to{" "}
          <strong>{toNames}</strong>
          {d.reason ? <span className="text-muted"> — {d.reason}</span> : null}
        </>
      );
    }
    return "Task transferred";
  }
  if (log.action === "update_stage") {
    return (
      <>
        Stage changed from <strong>{d?.fromStage ?? "N/A"}</strong> to{" "}
        <strong>{d?.toStage ?? "N/A"}</strong>
      </>
    );
  }
  if (log.action === "update") {
    const changes: string[] = d?.changes || [];
    if (changes.length === 0) return "Task details were updated";
    return (
      <ul className="mb-0 ps-3">
        {changes.map((c, i) => (
          <li key={i}>{c}</li>
        ))}
      </ul>
    );
  }
  if (log.action === "delete") return "Task was deleted";
  if (log.action === "create") return "Task was created";
  if (log.action === "recurring_start") return "Recurrence was started";
  if (log.action === "recurring_stop") return "Recurrence was stopped";
  return null;
};

const TaskHistoryModal: React.FC<TaskHistoryModalProps> = ({
  todo,
  signature,
  onClose,
}) => {
  const [logs, setLogs] = useState<any[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchLogs = async (todoId: string, pageNum: number) => {
    try {
      setLoading(true);
      const response = await api.post("todoService/todoLogs", {
        signature,
        todo_id: todoId,
        page: pageNum,
        limit: PAGE_SIZE,
      });
      setLogs(response?.data?.data?.data || []);
      setTotal(response?.data?.data?.total || 0);
      setPage(pageNum);
    } catch (error) {
      notify.error("Failed to load task history");
    } finally {
      setLoading(false);
    }
  };

  // Load page 1 whenever a task is opened
  useEffect(() => {
    if (todo?.id) {
      setLogs([]);
      setTotal(0);
      setPage(1);
      fetchLogs(todo.id, 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todo?.id]);

  return (
    <Modal show={!!todo} onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          Task History
          {todo ? (
            <span className="text-muted fs-14 ms-2">#{todo.id}</span>
          ) : null}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "60vh", overflowY: "auto" }}>
        {loading ? (
          <div className="text-center py-4">
            <span className="spinner-border spinner-border-sm me-2" />
            Loading history...
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center text-muted py-4">
            <i
              className="ti ti-history d-block mb-2"
              style={{ fontSize: "32px" }}
            />
            No history available for this task yet.
          </div>
        ) : (
          <ul className="list-unstyled mb-0">
            {logs.map((log) => {
              const meta = logActionMeta(log.action);
              const detail = renderLogDetail(log);
              return (
                <li
                  key={log.id}
                  className="d-flex align-items-start border-bottom pb-3 mb-3"
                >
                  <span
                    className={`me-3 ${meta.color}`}
                    style={{ fontSize: "20px" }}
                  >
                    <i className={`ti ${meta.icon}`} />
                  </span>
                  <div className="flex-grow-1">
                    <div className="d-flex justify-content-between flex-wrap">
                      <span className="fw-medium">{meta.label}</span>
                      <small className="text-muted">
                        {log.performed_at
                          ? moment(log.performed_at).format(
                              "DD MMM YYYY, hh:mm A"
                            )
                          : ""}
                      </small>
                    </div>
                    {detail ? (
                      <div className="fs-14 mt-1">{detail}</div>
                    ) : null}
                    {log.performed_by_name ? (
                      <small className="text-muted d-block mt-1">
                        By {log.performed_by_name}
                      </small>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Modal.Body>
      <Modal.Footer className="d-flex align-items-center justify-content-between">
        {total > PAGE_SIZE ? (
          <Pagination
            current={page}
            pageSize={PAGE_SIZE}
            total={total}
            onChange={(p) => fetchLogs(todo?.id || "", p)}
            size="small"
            showSizeChanger={false}
            disabled={loading}
          />
        ) : (
          <span />
        )}
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TaskHistoryModal;
